import { Skeleton } from "@heroui/react";
import { Link } from "react-router";
import { ArrowRightIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import type { ReactNode } from "react";
import type { Recipe } from "../Recipes-renamed/types";
import { RecipeCard2 } from "../RecipeCard/RecipeCard2";

type Surface = "cream" | "parchment" | "warm";

interface HomeRecipeSamplingProps {
    readonly title: string;
    readonly subhead: string;
    readonly seeAllHref?: string;
    readonly seeAllLabel?: string;
    readonly surface: Surface;
    readonly recipes: Recipe[];
    readonly isLoading: boolean;
    readonly isError: boolean;
    readonly emptyState?: ReactNode;
    readonly cardCount?: number;
    readonly showAddedAgo?: boolean;
}

const surfaceToBg: Record<Surface, string> = {
    cream: "bg-[var(--bg)]",
    parchment: "bg-[var(--bg-parchment)]",
    warm: "bg-[var(--bg-warm)]"
};

const SkeletonCard = () => (
    <div className="flex flex-col">
        <Skeleton className="h-44 w-full rounded-lg mb-3" />
        <Skeleton className="h-4 w-24 rounded mb-2" />
        <Skeleton className="h-6 w-3/4 rounded mb-2" />
        <Skeleton className="h-4 w-1/2 rounded" />
    </div>
);

export const HomeRecipeSampling = (props: HomeRecipeSamplingProps) => {
    const {
        title,
        subhead,
        seeAllHref,
        seeAllLabel,
        surface,
        recipes,
        isLoading,
        isError,
        emptyState,
        cardCount = 3,
        showAddedAgo
    } = props;

    const isEmpty = !isLoading && !isError && recipes.length === 0;

    if (isEmpty && !emptyState) return null;

    return (
        <section className={`${surfaceToBg[surface]} px-6 sm:px-12 py-16 sm:py-20`}>
            <div className="m-auto max-w-5xl">
                <header className="mb-10 sm:mb-12 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                    <div>
                        <h2 className="font-Cinzel text-3xl sm:text-4xl uppercase tracking-[0.2em] text-[var(--color-secondary-500)] mb-3">
                            {title}
                        </h2>
                        <p className="font-serif italic text-lg sm:text-xl text-[var(--fg-muted)]">{subhead}</p>
                    </div>
                    {seeAllHref && seeAllLabel && (
                        <Link
                            to={seeAllHref}
                            className="group inline-flex items-center gap-2 font-serif italic text-base sm:text-lg text-[var(--accent)] hover:text-[var(--color-accent-600)] transition-colors whitespace-nowrap"
                        >
                            <span>{seeAllLabel}</span>
                            <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                        </Link>
                    )}
                </header>

                {isError && (
                    <div
                        role="alert"
                        className="flex flex-col items-center justify-center text-[var(--fg-muted)]"
                    >
                        <ExclamationTriangleIcon className="h-10 w-10 text-danger-500 mb-3" />
                        <span className="font-serif italic">Could not load recipes.</span>
                    </div>
                )}

                {isLoading && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10">
                        {Array.from({ length: cardCount }).map((_, i) => (
                            <SkeletonCard key={i} />
                        ))}
                    </div>
                )}

                {!isLoading && !isError && recipes.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10">
                        {recipes.map((recipe) => (
                            <RecipeCard2
                                key={recipe.id}
                                recipe={recipe}
                                size="sm"
                                showAddedAgo={showAddedAgo}
                            />
                        ))}
                    </div>
                )}

                {isEmpty && emptyState}
            </div>
        </section>
    );
};
